/**
 * supportOutbox — IndexedDB-backed queue of ticket-create payloads that
 * failed to send (offline / network drop). Drained on `window.online`
 * and on app boot. Attachments are NOT stored (too large for the outbox
 * use case; user re-uploads when back online if needed).
 *
 * Fallback to localStorage if IndexedDB is unavailable (private mode,
 * Safari quota tantrums) — text payloads are small enough that 5 MB
 * localStorage cap covers ~10k drafts.
 *
 * Initiative: docs/specs/support-agent-onboardix-2026q2-pr8-offline-tg/spec.md
 */

const DB_NAME = 'onboardix_support_outbox'
const STORE = 'pending_tickets'
const LS_KEY = 'onboardix_support_outbox_v1'

let _dbPromise = null

function _openDb() {
    if (typeof indexedDB === 'undefined') return Promise.resolve(null)
    if (_dbPromise) return _dbPromise
    _dbPromise = new Promise((resolve) => {
        const req = indexedDB.open(DB_NAME, 1)
        req.onupgradeneeded = () => {
            const db = req.result
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true })
            }
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => resolve(null)
    })
    return _dbPromise
}

function _lsRead() {
    if (typeof localStorage === 'undefined') return []
    try {
        return JSON.parse(localStorage.getItem(LS_KEY) || '[]')
    } catch {
        return []
    }
}

function _lsWrite(items) {
    if (typeof localStorage === 'undefined') return
    try {
        localStorage.setItem(LS_KEY, JSON.stringify(items))
    } catch {
        /* quota — silent */
    }
}

export async function enqueue(payload) {
    const stamped = { ...payload, _outboxedAt: new Date().toISOString() }
    const db = await _openDb()
    if (!db) {
        const all = _lsRead()
        all.push({ id: Date.now() + Math.random(), ...stamped })
        _lsWrite(all)
        return all.length
    }
    return new Promise((resolve) => {
        const tx = db.transaction(STORE, 'readwrite')
        tx.objectStore(STORE).add(stamped)
        tx.oncomplete = async () => resolve(await count())
        tx.onerror = () => {
            const all = _lsRead()
            all.push({ id: Date.now() + Math.random(), ...stamped })
            _lsWrite(all)
            resolve(all.length)
        }
    })
}

export async function count() {
    const db = await _openDb()
    if (!db) return _lsRead().length
    return new Promise((resolve) => {
        const tx = db.transaction(STORE, 'readonly')
        const req = tx.objectStore(STORE).count()
        req.onsuccess = () => resolve(req.result || 0)
        req.onerror = () => resolve(_lsRead().length)
    })
}

async function _peekAll() {
    const db = await _openDb()
    if (!db) return _lsRead()
    return new Promise((resolve) => {
        const tx = db.transaction(STORE, 'readonly')
        const req = tx.objectStore(STORE).getAll()
        req.onsuccess = () => resolve(req.result || [])
        req.onerror = () => resolve(_lsRead())
    })
}

async function _delete(id) {
    const db = await _openDb()
    if (!db) {
        const all = _lsRead().filter((p) => p.id !== id)
        _lsWrite(all)
        return
    }
    return new Promise((resolve) => {
        const tx = db.transaction(STORE, 'readwrite')
        tx.objectStore(STORE).delete(id)
        tx.oncomplete = () => resolve()
        tx.onerror = () => {
            const all = _lsRead().filter((p) => p.id !== id)
            _lsWrite(all)
            resolve()
        }
    })
}

/**
 * Drain outbox by calling submitFn(payload) for each. submitFn must throw
 * on transient failure (network); throws are caught and the item stays.
 * Returns count of items that successfully sent.
 */
export async function drain(submitFn) {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return 0
    const items = await _peekAll()
    let sent = 0
    for (const item of items) {
        const { id, _outboxedAt, ...payload } = item
        try {
            await submitFn(payload)
            await _delete(id)
            sent += 1
        } catch {
            // Stop on first transient — leave rest for next drain.
            break
        }
    }
    return sent
}

/**
 * Install global drain hook: listens for `window.online` event and calls
 * the provided submit function. Safe to call multiple times — installs once.
 */
let _listenerInstalled = false
export function installAutoDrain(submitFn) {
    if (_listenerInstalled || typeof window === 'undefined') return
    _listenerInstalled = true
    const handler = () => {
        drain(submitFn).then((sent) => {
            if (sent > 0 && typeof window !== 'undefined') {
                window.dispatchEvent(
                    new CustomEvent('onboardix:support:outbox:drained', { detail: { sent } }),
                )
            }
        })
    }
    window.addEventListener('online', handler)
    // Also try once at install time (boot).
    if (navigator.onLine) handler()
}
